import React, { createContext, useContext, useState, useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ClientDashboard from "@/pages/ClientDashboard";
import TechDashboard from "@/pages/TechDashboard";
import DeviceSheet from "@/pages/DeviceSheet";
import ResinLotsPage from "@/pages/ResinLotsPage";
import AdminPanel from "@/pages/AdminPanel";
import OrderForm from "@/pages/OrderForm";
import Catalog from "@/pages/Catalog";

import BatchManagement from "@/pages/BatchManagement";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import MentionsLegalesPage from "@/pages/MentionsLegalesPage";
import CGVPage from "@/pages/CGVPage";
import ActivateAccountPage from "@/pages/ActivateAccountPage";
import ConsentPage from "@/pages/ConsentPage";
import ConsentBanner from "@/pages/ConsentBanner";
import CookieBanner from "@/components/CookieBanner";
import Viewer3D from "@/pages/Viewer3D";
import OrderTracking from "@/pages/OrderTracking";
import LandingPage from "@/pages/LandingPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          setUser(response.data);
          setToken(savedToken);
        } catch (error) {
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      if (response.data?.requires_2fa) {
        return { success: false, requires_2fa: true, temp_token: response.data.temp_token, email_hint: response.data.email_hint };
      }
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      toast.success('Connexion réussie');
      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur de connexion';
      toast.error(typeof message === 'string' ? message : 'Erreur de connexion');
      return { success: false, error: message };
    }
  };

  const verify2fa = async (tempToken, code) => {
    try {
      const response = await axios.post(`${API}/auth/2fa/verify`, { temp_token: tempToken, code });
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      toast.success('Connexion réussie');
      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.detail || 'Code invalide';
      toast.error(typeof message === 'string' ? message : 'Code invalide');
      return { success: false, error: message, status: error.response?.status };
    }
  };

  const resend2fa = async (tempToken) => {
    try {
      const r = await axios.post(`${API}/auth/2fa/resend`, { temp_token: tempToken });
      toast.success(`Nouveau code envoyé (${r.data.resends_left} renvoi(s) restant(s))`);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de renvoyer le code');
      return false;
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      const { token: newToken, user: newUser } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(newUser);
      toast.success('Compte créé avec succès');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la création du compte';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    toast.success('Déconnexion réussie');
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch {}
  };

  const value = {
    user,
    token,
    loading,
    login,
    verify2fa,
    resend2fa,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!token && !!user,
    isClient: user?.role === 'client',
    isTechnician: user?.role === 'technician',
    isAdmin: user?.role === 'admin',
    // Consent requis pour les CLIENTS uniquement (technicien/admin non concernés).
    // Bloque la création de commande côté backend, mais permet la consultation.
    needsConsent: !!user && user.role === 'client' && (!user.privacy_accepted || !user.cgv_accepted)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Axios interceptor for auth
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Protected Route with consent check
const ROLES_CLIENT = ['client'];
const ROLES_TECH_ADMIN = ['technician', 'admin'];
const ROLES_ADMIN = ['admin'];

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-100">
        <div className="text-zinc-500">Chargement...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // NB : Le consentement RGPD + CGV n'est plus bloquant. Un ConsentBanner
  // s'affiche sur les dashboards clients, et POST /api/orders refuse en 403.

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    if (user?.role === 'client') {
      return <Navigate to="/client" replace />;
    } else if (user?.role === 'technician') {
      return <Navigate to="/dashboard" replace />;
    } else if (user?.role === 'admin') {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return (
    <>
      <ConsentBanner />
      {children}
    </>
  );
};

// Main App Component
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/confidentialite" element={<PrivacyPolicyPage />} />
          <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
          <Route path="/cgv" element={<CGVPage />} />
          <Route path="/activate" element={<ActivateAccountPage />} />
          <Route path="/consent" element={
            <ProtectedRoute><ConsentPage /></ProtectedRoute>
          } />
          
          {/* Client Routes */}
          <Route path="/client" element={
            <ProtectedRoute allowedRoles={ROLES_CLIENT}>
              <ClientDashboard />
            </ProtectedRoute>
          } />
          <Route path="/client/order" element={
            <ProtectedRoute allowedRoles={ROLES_CLIENT}>
              <OrderForm />
            </ProtectedRoute>
          } />
          <Route path="/client/catalog" element={
            <ProtectedRoute allowedRoles={ROLES_CLIENT}>
              <Catalog />
            </ProtectedRoute>
          } />
          
          {/* Technician Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute allowedRoles={ROLES_TECH_ADMIN}>
              <TechDashboard />
            </ProtectedRoute>
          } />
          <Route path="/batches" element={
            <ProtectedRoute allowedRoles={ROLES_TECH_ADMIN}>
              <BatchManagement />
            </ProtectedRoute>
          } />
          <Route path="/device/:ident" element={
            <ProtectedRoute allowedRoles={ROLES_TECH_ADMIN}>
              <DeviceSheet />
            </ProtectedRoute>
          } />
          <Route path="/resin-lots" element={
            <ProtectedRoute allowedRoles={ROLES_TECH_ADMIN}>
              <ResinLotsPage />
            </ProtectedRoute>
          } />
          
          {/* Admin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={ROLES_ADMIN}>
              <AdminPanel />
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute allowedRoles={ROLES_ADMIN}>
              <AdminPanel tab="users" />
            </ProtectedRoute>
          } />
          
          {/* Public 3D Viewer - no auth needed */}
          <Route path="/viewer/:token" element={<Viewer3D />} />

          {/* Public order tracking via secure token - no auth needed */}
          <Route path="/track/:token" element={<OrderTracking />} />

          {/* Default redirect */}
          <Route path="/" element={<LandingPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <CookieBanner />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

// Role-based redirect component
const RoleBasedRedirect = () => {
  const { user, loading, isAuthenticated } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-100">
        <div className="text-zinc-500">Chargement...</div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  switch (user?.role) {
    case 'client':
      return <Navigate to="/client" replace />;
    case 'technician':
      return <Navigate to="/dashboard" replace />;
    case 'admin':
      return <Navigate to="/dashboard" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
};

export default App;
